import type { Session, User } from '@supabase/supabase-js';
import { DEFAULT_POST_AUTH_PATH } from '@/lib/default-landing';

/**
 * Reset-required routing for migrated users.
 *
 * Phase 7 of the MSSQL→Supabase migration created ~860 legacy users
 * passwordless because their stored password was AES-encrypted under a key
 * that had been rotated away (see `tasks/v3-coding-plans/artifacts/
 * phase-7-status.md`). Each was tagged
 * `app_metadata.requires_password_reset = true` in `auth.users`. Those users
 * cannot sign in with a password — once they obtain a session (via the
 * Supabase recovery / activation email), the app must route them to a
 * "set your password" screen instead of dropping them into the app or showing
 * a confusing failed sign-in.
 *
 * The provenance flag lives in `app_metadata` (server-controlled — a user
 * cannot set it themselves), which is exactly where a trust-bearing flag
 * belongs. `user_metadata` is user-writable and must never be trusted here.
 */

/** The route a reset-required user is sent to once they have a session. */
export const SET_PASSWORD_ROUTE = '/set-password';

const REQUIRES_PASSWORD_RESET_KEY = 'requires_password_reset';

/**
 * Whether the authenticated principal must set a new password before using
 * the app. Reads `app_metadata.requires_password_reset` from a Supabase
 * `Session` or `User`. Defaults to `false` for any null/unknown shape — the
 * gate fails open to "no reset required" so a malformed token never traps a
 * legitimate user on the set-password screen.
 */
export function requiresPasswordReset(source: Session | User | null | undefined): boolean {
  if (!source) return false;
  // A Session carries `access_token`; a bare User does not. Narrow on that.
  const user: User | undefined = 'access_token' in source ? source.user : source;
  const appMetadata = user?.app_metadata as Record<string, unknown> | undefined;
  return appMetadata?.[REQUIRES_PASSWORD_RESET_KEY] === true;
}

/**
 * The route to navigate to immediately after authentication. Reset-required
 * users go to the set-password screen; everyone else continues to `fallback`.
 */
export function nextRouteAfterAuth(
  source: Session | User | null | undefined,
  fallback: string = DEFAULT_POST_AUTH_PATH,
): string {
  return requiresPasswordReset(source) ? SET_PASSWORD_ROUTE : fallback;
}
